<template>
    <FormSection :application-id="application" :showBackground="false" size="0">
        <template v-if="isLoading" #content>
            <div class="p-5 d-flex flex-column text-center text-primary">
                <i class="far fa-save fa-7x me-2 animated slow flash infinite"></i>
                <div class="big-text mt-1">
                    <b>Save Payer In Progress...</b>
                </div>
            </div>
        </template>
        <template v-if="!isLoading" #content>
            <div class="p-2 pb-0">
                <div class="row">
                    <div class="col-12 col-md-4">
                        <div class="form-group">
                            <label for="payer">
                                Payer
                                <span class="text-danger">*</span>
                            </label>
                            <TypeaheadInput
                                :getData="getPayors"
                                :value="initialValue"
                                v-on:valueChanged="selectedPayorChangedd"
                                :init-value="selectedPayor"
                                placeholder="Start Typing..."
                                :debounce="100"
                                :disabled="!isAddForm"
                                v-on:blurredInput="v$.payer.AgencyPayorName.$touch"
                                :class="`${!v$.payer.AgencyPayorName.$error ? '' : 'field-invalid'}`"
                            />
                            <span v-if="v$.payer.AgencyPayorName.$error" class="field-invalid">Payer is required.</span>
                        </div>
                    </div>
                    <div class="col-12 col-md-4">
                        <div class="form-group">
                            <label for="effective-date">
                                Effective Date
                                <span class="text-danger">*</span>
                            </label>
                            <DatePicker :getDate="changeEffectiveDate">
                                <div class="input-group date datepickerInput">
                                    <input
                                        type="text"
                                        name="effective-date"
                                        class="form-control"
                                        :value="formatDateInput(payer.EffectiveDate)"
                                        @change="handleDateChange"
                                        placeholder="MM/DD/YYYY"
                                        autocomplete="off"
                                        @blur="v$.payer.EffectiveDate.$touch"
                                        :class="`${!v$.payer.EffectiveDate.$error ? '' : 'field-invalid'}`"
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                    <span v-if="v$.payer.EffectiveDate.$error" class="field-invalid"
                                        >Effective Date is required.</span
                                    >
                                </div>
                            </DatePicker>
                        </div>
                    </div>
                    <div class="col-12 col-md-4">
                        <div class="form-group">
                            <label for="payer-type">
                                Payer Type
                                <span class="text-danger">*</span>
                            </label>
                            <select
                                class="form-select"
                                name="payer-type"
                                v-model="payer.Type"
                                @change="payerTypeUpdated()"
                                :disabled="!isAddForm"
                                @blur="v$.payer.Type.$touch"
                                :class="`${!v$.payer.Type.$error ? '' : 'field-invalid'}`"
                            >
                                <option :value="undefined">Select Payer Type</option>
                                <option
                                    v-for="[key, value] of enumToMap(payorTypeEnum)"
                                    :key="key"
                                    :value="value.value"
                                >
                                    {{ value.name }}
                                </option>
                            </select>
                            <span v-if="v$.payer.Type.$error" class="field-invalid">Type is required.</span>
                        </div>
                    </div>
                    <div class="col-12 col-md-10 offset-md-1" v-if="showRoomAndBoardWarning && !payer.IsCharity">
                        <div class="alert alert-warning mb-2" role="alert">
                            <i class="fas fa-exclamation-triangle text-warning me-2"></i> This payer is not set up as a
                            room and board payer.
                            <a href="/list/payer-edit/${selectedPayor.value}/billing-information" target="_blank"
                                >Click here</a
                            >
                            to update the payer’s setup to include room & board billing.
                        </div>
                    </div>
                </div>
                <div v-if="payer.AgencyPayorId && !payer.IsCharity">
                    <div v-if="!payer.IsMedicare">
                        <NonMedicarePayer
                            :payer="payer"
                            :relationshipToInsuredEnum="relationshipToInsuredEnum"
                            :payerSequenceCodeEnum="payerSequenceCodeEnum"
                            :hasDeductibleMetChanged="hasDeductibleMetChanged"
                            :showSubscriberName="showSubscriberName"
                            :roomAndBoardTypeValue="roomAndBoardTypeValue"
                            :requireAuthorizationChanged="requireAuthorizationChanged"
                            :outOfPocketMaxMetChanged="outOfPocketMaxMetChanged"
                        />
                    </div>
                    <div class="row w-100" v-if="payer.IsMedicare">
                        <MedicarePayer
                            :payer="payer"
                            :requireAuthorizationChanged="requireAuthorizationChanged"
                            :outOfPocketMaxMetChanged="outOfPocketMaxMetChanged"
                            :getDate="medicareDateChange"
                        />
                    </div>
                    <div class="col-12 row form-group">
                        <div class="col-md-10 offset-md-1 px-0">
                            <label for="comments"> Comments </label>
                            <textarea
                                class="form-control"
                                id="comments"
                                v-model="payer.Comments"
                                rows="4"
                                maxlength="2000"
                            ></textarea>
                        </div>
                        <div class="col-md-10 offset-md-1 px-0">
                            <div class="form-check custom-checkbox mt-2">
                                <input
                                    type="checkbox"
                                    :id="`benefits-verified_${payer.AgencyId}`"
                                    class="form-check-input"
                                    v-model="payer.IsBenefitVerified"
                                />
                                <label class="form-check-label" :for="`benefits-verified_${payer.AgencyId}`">
                                    <span class="me-1">Benefits Verified</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </FormSection>
</template>

<script lang="ts" src="./PayerForm.ts"></script>
